/**
 * Blogs data layer.
 *
 * Categories power the timeline composer's category picker (Phase 1 #4) and the
 * `/blogs` filter chips. The list/detail/delete hooks back the public Blogs,
 * category, detail, and dashboard My-Blogs pages (Phase 1 #8).
 *
 * The list is page-based and consumed via `useInfiniteQuery`, keyed off the
 * `next` cursor returned by `blogs/list`.
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  type InfiniteData,
  type QueryKey,
} from '@tanstack/react-query';

import { api } from '@/lib/api/client';
import { queryKeys } from '@/lib/api/queryKeys';
import type { Blog, BlogCategory, BlogListResult } from '@/types/api';

export type { BlogCategory };

/** Default page size for blog lists. */
const PAGE_LIMIT = 12;

/** Parameters accepted by `useBlogList`. */
export interface BlogListParams {
  /** Category SEO slug (`/blogs/:category`). */
  categorySeo?: string;
  /** Free-text search. */
  q?: string;
  /** Filter to the signed-in user's own blogs (`My Blogs`; requires auth). */
  myblogs?: boolean;
  /** Page size (defaults to 12). */
  limit?: number;
}

type BlogListInfinite = InfiniteData<BlogListResult, number>;

/** Serialise params to a query string, skipping empty values. */
function toQuery(params: Record<string, unknown> = {}): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

/** Active blog categories (`GET /blogs/categories`). */
export function useBlogCategories(enabled = true) {
  return useQuery({
    queryKey: queryKeys.blogs.categories(),
    enabled,
    staleTime: 5 * 60_000,
    // Sidebar-like reference data — consulted from /blogs, blog
    // detail, MyBlogs, RelatedBlogs. Keep cached for 30 min.
    gcTime: 30 * 60_000,
    queryFn: () => api.get<BlogCategory[]>('blogs/categories'),
  });
}

/**
 * Infinite blog list (`GET /blogs/list`). Paginates on the `next` cursor.
 * Pass `categorySeo` for a category page, `myblogs` for the dashboard list.
 */
export function useBlogList(params: BlogListParams = {}) {
  const limit = params.limit ?? PAGE_LIMIT;
  const query = {
    category_seo: params.categorySeo,
    q: params.q,
    ...(params.myblogs ? { myblogs: 'y', source: 'dashboard' } : {}),
    limit,
  };
  return useInfiniteQuery<
    BlogListResult,
    Error,
    BlogListInfinite,
    QueryKey,
    number
  >({
    queryKey: queryKeys.blogs.list(query),
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      api.get<BlogListResult>(`blogs/list${toQuery({ ...query, page: pageParam })}`),
    getNextPageParam: (lastPage, _all, lastParam) =>
      lastPage.next ? lastParam + 1 : undefined,
  });
}

/** A single blog by slug (`GET /blogs/view/:slug`). */
export function useBlogDetail(slug: string | undefined) {
  return useQuery({
    queryKey: queryKeys.blogs.detail(slug ?? ''),
    enabled: !!slug,
    // One-shot per-slug detail — a session that hops between blogs
    // would otherwise accumulate 5 min worth of payloads. 1 min
    // still rewards the back-button fast-path.
    gcTime: 60_000,
    queryFn: () => api.get<Blog>(`blogs/view/${slug}`),
  });
}

/** Delete one of the signed-in user's blogs (`POST /blogs/delete/:id`). */
export function useDeleteBlog() {
  return useMutation({
    mutationFn: (id: number) => api.post<unknown>(`blogs/delete/${id}`),
  });
}

/**
 * Admin-only approve / decline action (`POST /blogs/act/:id/:act`). `:act`
 * is `'A'` (approve) or `'D'` (decline). The backend gates this server-side;
 * the call returns 200 with a permission-error message for non-admins, so
 * the UI must also gate via `blog.isAdmin` before exposing the buttons.
 */
export function useBlogAdminAction() {
  return useMutation({
    mutationFn: ({ id, act }: { id: number; act: 'A' | 'D' }) =>
      api.post<unknown>(`blogs/act/${id}/${act}`),
  });
}
